import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  AddCardBox,
  Box,
  Button,
  CardBox,
  CardBoxLabel,
  Chip,
  CircularProgress,
  EmptyState,
  MenuItem,
  ModalBox,
  PageHeader,
  SearchIcon,
  TextField,
} from 'components';
import { useIsMobile } from 'hooks';
import {
  Add,
  Archive,
  ArchiveMinus,
  CloseCircle,
  Edit2,
  Filter,
  FilterRemove,
  Home2,
  Mobile,
  Personalcard,
  Sms,
  User,
} from 'iconsax-react';

import { PayeeCreateEditForm } from './PayeeCreateEditForm';

import {
  ContentLoader,
  _,
  styled,
  useSelector,
  useTranslation,
} from 'third-party';

import {
  PayeeColors,
  PayeeStatus,
  USER_INPUT_DEBOUNCE_TIME_MS,
} from 'constants/common';

import { usePayee } from 'modules/payee';

import { appSelectors, payeeSelectors } from 'selectors';

import { APIPayee, APIPayeeStatus } from 'types/api';

import { palette } from 'styles/theme';

const StyleInput = styled(TextField)`
  .MuiInput-input {
    padding: 0.4rem !important;
    min-width: 18rem;
  }
`;

const cardActions = (item: APIPayee) => {
  return (
    <Box
      display="flex"
      width="100%"
      alignItems="center"
      justifyContent="space-between"
    >
      <Chip
        label={PayeeStatus[item.status as APIPayeeStatus]}
        style={{
          height: '1.5rem',
          padding: '0rem',
          marginLeft: '0.3rem',
          marginTop: '0.2rem',
          backgroundColor: PayeeColors[item.status as APIPayeeStatus],
        }}
      />
    </Box>
  );
};

const payeeIconColor = (payee: APIPayee) => {
  return payee.status == APIPayeeStatus.Active
    ? palette.primary900
    : palette.grey400;
};

export const UserPayeeList = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isFilteringOn, setIsFilteringOn] = useState(false);
  const [selectedItem, setSelectedItem] = useState<APIPayee | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const items = useSelector(payeeSelectors.payeeList);
  const allPayeeLoaded = useSelector(payeeSelectors.allPayeesLoaded);
  const payeeFilters = useSelector(payeeSelectors.payeeFilters);
  const globalFilter = useSelector(payeeSelectors.payeeGlobalFilter);
  const loadUserPayeesPending = useSelector(
    payeeSelectors.loadUserPayeesPending,
  );
  const loadMoreUserPayeesPending = useSelector(
    payeeSelectors.loadMoreUserPayeesPending,
  );
  const payeesListLoaded = useSelector(payeeSelectors.payeesListLoaded);
  const organizationId = useSelector(appSelectors.organizationId);

  const updatePayeePending = useSelector(payeeSelectors.updatePayeePending);

  const query = globalFilter || '';

  // Inside an organization the add tile replaces the dead-end empty block; the
  // block still wins when a search that is actually applied matched nothing.
  const isEmptyResult = !items?.length && payeesListLoaded;
  const showEmptyState = isEmptyResult && (!organizationId || !!query);
  const showAddPayeeTile =
    !!organizationId && payeesListLoaded && !showEmptyState;

  const {
    updatePayee,
    clearPayeeList,
    loadUserPayees,
    loadMoreUserPayees,
    updatePayeeFilters,
    setPayeeGlobalFilter,
    resetPayeeFilters,
  } = usePayee();

  useEffect(() => {
    loadUserPayees();
  }, [payeeFilters, loadUserPayees]);

  useEffect(() => {
    return () => {
      clearPayeeList();
      setPayeeGlobalFilter('');
    };
  }, [clearPayeeList, setPayeeGlobalFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearchInputChange = useCallback(
    _.debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target) {
        updatePayeeFilters({
          ...payeeFilters,
        });
        setPayeeGlobalFilter(event.target.value);
      }
    }, USER_INPUT_DEBOUNCE_TIME_MS),
    [updatePayeeFilters, payeeFilters, setPayeeGlobalFilter],
  );

  const cardMenuItems = (item: APIPayee, handleClose: () => void) => {
    return [
      <MenuItem
        key="Edit"
        disabled={updatePayeePending}
        onClick={() => {
          setSelectedItem(item);
          setIsModalOpen(true);
          handleClose();
        }}
      >
        <Edit2 variant="TwoTone" size="24" />
        {t('payeeList.menuItem.edit')}
      </MenuItem>,
      <MenuItem
        key="Remove"
        disabled={
          updatePayeePending ||
          (item.status !== APIPayeeStatus.Archive &&
            item.status !== APIPayeeStatus.Active)
        }
        onClick={() => {
          updatePayee(item.organization.id, item.id, {
            ...item,
            userId: item.user.id,
            status:
              item.status == APIPayeeStatus.Archive
                ? APIPayeeStatus.Active
                : APIPayeeStatus.Archive,
          });
        }}
      >
        {item.status == APIPayeeStatus.Archive ? (
          <Archive variant="TwoTone" size="24" />
        ) : (
          <ArchiveMinus variant="TwoTone" size="24" />
        )}

        {item.status == APIPayeeStatus.Archive
          ? t('common.activate')
          : t('common.archive')}
      </MenuItem>,
    ];
  };

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      const bottom =
        Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <
        1;

      if (bottom && !loadMoreUserPayeesPending && !allPayeeLoaded) {
        loadMoreUserPayees();
      }
    },
    [allPayeeLoaded, loadMoreUserPayeesPending, loadMoreUserPayees],
  );

  const onFilterReset = useCallback(() => {
    resetPayeeFilters();
    setPayeeGlobalFilter('');
    if (ref.current) {
      ref.current.value = '';
    }
  }, [resetPayeeFilters, setPayeeGlobalFilter]);

  const onAddPayeeClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const onModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
  }, []);

  return (
    <Box width="100%" overflow="auto" onScroll={handleScroll}>
      <Box
        position="sticky"
        top={0}
        zIndex={11}
        bgcolor="white"
        alignItems="center"
      >
        <PageHeader
          header={t('payeeList.header')}
          subHeader={t('payeeList.subHeader')}
          icon={
            <Personalcard
              variant="TwoTone"
              color={palette.primary900}
              size="28"
            />
          }
        >
          <>
            {isFilteringOn && (
              <Button variant="text" onClick={onFilterReset}>
                <CloseCircle variant="TwoTone" size="24" />
              </Button>
            )}
            <Button variant="text" onClick={onAddPayeeClick}>
              <Add variant="TwoTone" size="24" />
            </Button>
            <Button variant="text" onClick={() => setIsFilteringOn(it => !it)}>
              {isFilteringOn ? (
                <Filter variant="TwoTone" size="24" />
              ) : (
                <FilterRemove variant="TwoTone" size="24" />
              )}
            </Button>
          </>
        </PageHeader>

        {isFilteringOn && (
          <Box
            display="flex"
            alignItems="center"
            gap="0.5rem"
            padding="1rem 1rem 0 0 "
          >
            <SearchIcon color={palette.primary900} size="20" />
            <StyleInput
              inputRef={ref}
              autoComplete="off"
              autoCorrect="off"
              id="input-with-sx"
              onChange={onSearchInputChange}
              variant="standard"
            />
          </Box>
        )}
      </Box>
      {loadUserPayeesPending && (
        <Box
          paddingTop="1rem"
          display="grid"
          gap="1.25rem"
          sx={{
            overflow: 'hidden',
            gridTemplateColumns:
              'repeat(auto-fill, minmax(min(100%, 17rem), 1fr))',
          }}
        >
          {Array.from({ length: 8 }, (_, index) => (
            <Box
              key={`skeleton-${index}`}
              width="100%"
              height="14rem"
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <ContentLoader
                speed={2}
                width={272}
                height={224}
                viewBox="0 0 272 224"
                backgroundColor="#f3f3f3"
                foregroundColor="#ecebeb"
              >
                {/* Header section with title */}
                <rect x="20" y="20" rx="4" ry="4" width="150" height="16" />
                <rect x="20" y="45" rx="4" ry="4" width="100" height="12" />

                {/* Status chip */}
                <rect x="200" y="20" rx="8" ry="8" width="50" height="20" />

                {/* Content section with labels (4 rows for address, phone, email, name) */}
                <rect x="20" y="78" rx="4" ry="4" width="140" height="12" />
                <rect x="20" y="100" rx="4" ry="4" width="120" height="12" />
                <rect x="20" y="122" rx="4" ry="4" width="100" height="12" />
                <rect x="20" y="144" rx="4" ry="4" width="130" height="12" />

                {/* Menu icon area */}
                <rect x="220" y="180" rx="4" ry="4" width="24" height="24" />
              </ContentLoader>
            </Box>
          ))}
        </Box>
      )}
      {!loadUserPayeesPending && (
        <Box
          paddingTop="1rem"
          display="grid"
          gap="1.25rem"
          sx={{
            overflow: 'hidden',
            gridTemplateColumns:
              'repeat(auto-fill, minmax(min(100%, 17rem), 1fr))',
          }}
        >
          {items?.map(it => (
            <CardBox<APIPayee>
              item={it}
              key={it.id}
              width="100%"
              height="14rem"
              showAvatar={false}
              title={it.nameOnCheck}
              subTitle={it.organization.name}
              menuItems={cardMenuItems}
              cardActions={cardActions}
              highlightQuery={query}
            >
              <CardBoxLabel
                color={payeeIconColor(it)}
                label={`${it.city}, ${it.address}, ${it.zip}`}
                icon={Home2}
                highlightQuery={query}
              />

              <CardBoxLabel
                color={payeeIconColor(it)}
                label={it.phone}
                icon={Mobile}
                highlightQuery={query}
                iconStyle={{ paddingLeft: '2px' }}
              />
              <CardBoxLabel
                color={payeeIconColor(it)}
                label={it.email}
                icon={Sms}
                highlightQuery={query}
              />
              <CardBoxLabel
                color={payeeIconColor(it)}
                label={it.nameOnCheck}
                icon={User}
                highlightQuery={query}
              />
            </CardBox>
          ))}
          {showEmptyState && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <EmptyState
                showResetFilterButton={!!query}
                onFilterReset={onFilterReset}
              />
            </Box>
          )}
          {showAddPayeeTile && (
            <AddCardBox
              minHeight="14rem"
              label={t('payeeList.addTile.label')}
              onClick={onAddPayeeClick}
            />
          )}
        </Box>
      )}
      {loadMoreUserPayeesPending && (
        <Box display="flex" justifyContent="space-around" padding="1rem">
          <CircularProgress />
        </Box>
      )}
      {isModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '30rem'}
          height="39rem"
          onClose={onModalClose}
        >
          <PayeeCreateEditForm
            onModalClose={onModalClose}
            payeeId={selectedItem?.id}
            isUserPayeeList
          />
        </ModalBox>
      )}
    </Box>
  );
};
